import {
  BillingService,
  CampaignService,
  ProgramService,
  TemplateService
} from "@adfenix/types";

export type PartialPick<T, K extends keyof T> = {
  [P in K]?: T[P];
};

export type PickWithOptional<
  Source,
  Some extends keyof Source,
  Option extends keyof Source
> = Pick<Source, Some> & PartialPick<Source, Option>;

export type ArrayElement<T> = T extends Array<unknown> ? T[number] : never;

export const channelToPascalCase = (
  channel: TemplateService.ChannelType
): ProgramService.ChannelType => {
  switch (channel) {
    case "instagram":
      return "Instagram";
    case "twitter":
      return "Twitter";
    case "displayAd":
      return "DisplayAd";
    case "facebook":
      return "Facebook";
    default:
      return "Unknown";
  }
};

export const channelToCamelCase = (
  channel: ProgramService.ChannelType
): TemplateService.ChannelType => {
  switch (channel) {
    case "Instagram":
      return "instagram";
    case "Twitter":
      return "twitter";
    case "DisplayAd":
      return "displayAd";
    case "Facebook":
      return "facebook";
    case "Google":
      return "google";
    case "LinkedIn":
      return "linkedIn";
    case "YouTube":
      return "youTube";
    default:
      return "unknown";
  }
};

export const subjectAttributeToPascalCase = (
  subject: TemplateService.SubjectAttributeType | TemplateService.SubjectType
): ProgramService.SubjectAttributeType => {
  switch (subject) {
    case "agent":
      return "Agent";
    case "hq":
      return "Hq";
    case "office":
      return "Office";
    case "property":
      return "Property";
    case "programInstance":
      return "ProgramInstance";
    case "campaign":
      return "Campaign";
    case "payment":
      return "Payment";
    default:
      return "Unknown";
  }
};

export const subjectTypeToPascalCase = (subject: TemplateService.SubjectType) =>
  subjectAttributeToPascalCase(subject) as ProgramService.SubjectType;

export const subjectAttributeToCamelCase = (
  subject: ProgramService.SubjectAttributeType | ProgramService.SubjectType
): TemplateService.SubjectAttributeType => {
  switch (subject) {
    case "Agent":
      return "agent";
    case "Hq":
      return "hq";
    case "Office":
      return "office";
    case "Property":
      return "property";
    case "ProgramInstance":
      return "programInstance";
    case "Campaign":
      return "campaign";
    case "Payment":
      return "payment";
    default:
      return "unknown";
  }
};

export const subjectToCamelCase = (subject: ProgramService.SubjectType) =>
  subjectAttributeToCamelCase(subject) as TemplateService.SubjectType;

export const isFacebookTemplate = (
  template:
    | TemplateService.FacebookContentTemplateModel
    | TemplateService.TwitterContentTemplateModel
    | TemplateService.InstagramContentTemplateModel
): template is TemplateService.FacebookContentTemplateModel => {
  return template.hasOwnProperty("headline");
};

export const isFillFacebookContentTemplateResponse = (
  response:
    | TemplateService.FillFacebookContentTemplateResponse
    | TemplateService.FillInstagramContentTemplateResponse
    | TemplateService.FillTwitterContentTemplateResponse
): response is TemplateService.FillFacebookContentTemplateResponse => {
  return response.hasOwnProperty("headline");
};

/**
 * Define a subset of a type where all properties and nested properties are optional.
 * Useful for mocking.
 *
 * https://grrr.tech/posts/2021/typescript-partial/
 */
export type Subset<K> = {
  [attr in keyof K]?: K[attr] extends object
    ? Subset<K[attr]>
    : K[attr] extends object | null
    ? Subset<K[attr]> | null
    : K[attr] extends object | null | undefined
    ? Subset<K[attr]> | null | undefined
    : K[attr];
};

export const mapDniReasons = (
  reason: CampaignService.DoNotInvoiceReason
): BillingService.DNIReason => {
  switch (reason) {
    case "Disapproved":
      return "disapproved";
    case "AdfenixBug":
      return "adfenixBug";
    case "ClientIssue":
      return "clientIssue";
    case "TestCampaign":
      return "testCampaign";
    case "ZeroAdSpend":
      return "zeroAdSpend";
    case "Duplicate":
      return "duplicate";
    case "ClientCancellation":
      return "clientCancellation";
    case "ApRePublish":
      return "apRePublish";
    case "Reordered":
      return "reordered";
    case "CreditCardReordered":
      return "creditCardReordered";
    default:
      return "adfenixBug";
  }
};
